import { LocateFixed } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../../api/client'
import { applyPendingOnboarding, savePendingOnboarding } from '../../auth/pendingOnboarding'
import { useAuth } from '../../auth/AuthContext'
import { JoinCodeStep } from '../../components/auth/JoinCodeStep'
import { DateField } from '../../components/DateField'
import { ErrorMessage } from '../../components/ErrorMessage'
import { OnboardingShell } from '../../components/OnboardingShell'
import { TermsCheckbox } from '../../components/TermsCheckbox'
import { useTaxonomy } from '../../taxonomy/TaxonomyContext'
import type { Gender, NeedCategory, RegisterRequest } from '../../types/api'
import { ADULT_AGE, ageFromDob, DOB_MAX, DOB_MIN, DOB_OPEN_AT } from '../../utils/age'
import { VISIBLE_LOOKING_FOR } from '../../utils/lookingFor'
import { fillIfEmpty, reverseGeocode } from '../../utils/reverseGeocode'
import { isValidMobile, isValidPincode, MOBILE_PATTERN, PINCODE_PATTERN } from '../../utils/validation'

const GENDER_OPTIONS: { label: string; value: Gender }[] = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
  { label: 'Prefer not to say', value: 'PREFER_NOT_TO_SAY' },
]

const GUARDIAN_RELATION_OPTIONS = ['Mother', 'Father', 'Guardian', 'Other']

export function StudentRegisterForm({
  onBack,
  referralCode,
  aggregatorCode,
}: {
  onBack: () => void
  referralCode?: string
  /** From an aggregator's onboarding link - see RegisterPage. */
  aggregatorCode?: string
}) {
  const { register, verifyRegistration } = useAuth()
  const navigate = useNavigate()
  const { itemsOf } = useTaxonomy()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [dob, setDob] = useState('')
  const [gender, setGender] = useState<Gender | ''>('')
  const [pincode, setPincode] = useState('')
  const [locality, setLocality] = useState('')
  const [guardianPhone, setGuardianPhone] = useState('')
  const [guardianRelation, setGuardianRelation] = useState('')
  const [lookingFor, setLookingFor] = useState<Set<NeedCategory>>(new Set())
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // The request a join code was sent for. Set means the code step is showing; the form state above
  // is kept, so "Change email" goes back to a filled-in form.
  const [sentBody, setSentBody] = useState<RegisterRequest | null>(null)

  const age = useMemo(() => ageFromDob(dob), [dob])
  const isMinor = age !== null && age < ADULT_AGE

  function toggleLookingFor(category: NeedCategory) {
    setLookingFor((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  /**
   * Pins the dot and fills the pincode and locality from it.
   *
   * <p>Only fills what is still empty, so a learner who has typed their own area keeps it. A
   * failed lookup keeps the pin and says so - the coordinates are the half the map needs, and the
   * pincode can still be typed.
   */
  function useCurrentLocation() {
    if (!navigator.geolocation) return
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        setCoords({ lat: latitude, lng: longitude })
        try {
          const found = await reverseGeocode(latitude, longitude)
          setPincode((p) => (/^\d{6}$/.test(p) ? p : found.pincode ?? ''))
          setLocality((v) => fillIfEmpty(v, found.area ?? found.city))
        } catch (err) {
          console.error('[student-register] could not look up the address', err)
          setError('Location pinned, but the address could not be looked up - please type it in.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setError('Could not get your location. Check the browser has permission, or type it in.')
      },
      { timeout: 8000 },
    )
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (!isValidMobile(phone)) {
      setError('Enter a valid 10-digit mobile number')
      return
    }
    // Required, and not merely for completeness: age is what decides whether this account is a
    // minor, and a minor is what puts every connection behind a parent. Left blank, ageFromDob
    // returns null, isMinor is false, and a fourteen-year-old joins as an adult with the whole
    // guardian gate switched off.
    if (!dob) {
      setError('Enter your date of birth - it decides whether a parent needs to approve your connections')
      return
    }
    if (age === null) {
      setError('That date of birth does not look right - please check it')
      return
    }
    if (!gender) {
      setError('Choose your gender')
      return
    }
    if (!isValidPincode(pincode)) {
      setError('Enter a valid 6-digit pincode - it places your dot on the map')
      return
    }
    if (!locality.trim()) {
      setError('Enter your area / locality - it is shown beside your dot')
      return
    }
    if (lookingFor.size === 0) {
      setError('Choose at least one thing you are looking for')
      return
    }
    if (isMinor && !guardianPhone) {
      setError('A parent/guardian mobile number is required since you are under 18')
      return
    }
    if (isMinor && !isValidMobile(guardianPhone)) {
      setError('Enter a valid 10-digit parent/guardian mobile number')
      return
    }
    if (!termsAccepted) {
      setError('Accept the Terms & Conditions and Privacy Policy to continue')
      return
    }

    setSubmitting(true)
    try {
      const body: RegisterRequest = {
        entityKind: 'STUDENT',
        persona: 'DEMAND',
        fullName,
        email,
        phone,
        locality: locality.trim(),
        // All three guaranteed present by the checks above, so no `|| undefined` fallback that
        // would quietly let a blank through if a check were ever removed.
        pincode,
        dob,
        gender,
        guardianPhone: guardianPhone || undefined,
        guardianRelation: guardianRelation || undefined,
        referralCode,
        aggregatorCode,
        termsAccepted,
      }
      await register(body)

      // Stashed rather than sent: nothing exists until the join code is entered, so every one of
      // these endpoints would answer 401 right now. They are written straight after - see verify().
      savePendingOnboarding({
        profile: coords ? { lat: coords.lat, lng: coords.lng } : undefined,
        // The chip only states a category - the server requires at least one item too (see
        // TaxonomyValidator), which is why this used to fail silently every time (a bare
        // {category, isPrimary} was always rejected with items_required). The taxonomy's first item
        // for that category is a real, valid stand-in, editable any time from "What I want to
        // learn" on the profile.
        needs: [...lookingFor].map((category) => {
          const items = itemsOf(category)
          return {
            category,
            items: items.length ? [items[0]] : undefined,
            isPrimary: lookingFor.size === 1,
          }
        }),
      })
      setSentBody(body)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not join the network. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  /** Step 2: the code creates the account (or adds the role to an existing one) and signs in. */
  async function verify(code: string) {
    await verifyRegistration(email, code)
    await applyPendingOnboarding()
    navigate('/dashboard', { replace: true })
  }

  if (sentBody) {
    return (
      <OnboardingShell title="Learner registration">
        <JoinCodeStep
          sentTo={sentBody.email}
          onVerify={verify}
          onResend={async () => {
            await register(sentBody)
          }}
          onBack={() => setSentBody(null)}
          submitLabel="Create account"
          buttonClassName="btn-sun w-full"
        />
      </OnboardingShell>
    )
  }

  return (
    <OnboardingShell
      title="Learner registration"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-sm text-ink-600">
          Just the essentials - you will add what you want to learn on your profile next.
        </p>

        <label className="block">
          <span className="label label-required">Full name</span>
          <input
            required
            id="fullName"
            name="name"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="input"
            placeholder="Enter your full name"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Email address</span>
            <input
              required
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </label>
          <label className="block">
            <span className="label label-required">Mobile number</span>
            <input
              required
              id="phone"
              name="tel"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              pattern={MOBILE_PATTERN}
              maxLength={10}
              title="Enter a valid 10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="input"
              placeholder="98xxxxxxxx"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="block">
            <label className="label label-required" htmlFor="dob">Date of birth</label>
            <DateField
              id="dob"
              value={dob}
              onChange={setDob}
              min={DOB_MIN}
              max={DOB_MAX}
              openAt={DOB_OPEN_AT}
              placeholder="Select your date of birth"
            />
          </div>
          <label className="block">
            <span className="label label-required">Gender</span>
            <select
              required
              id="gender"
              name="gender"
              autoComplete="sex"
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender | '')}
              className="input"
            >
              <option value="">Select gender</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Location pincode</span>
            <input
              required
              id="pincode"
              name="postal-code"
              autoComplete="postal-code"
              inputMode="numeric"
              pattern={PINCODE_PATTERN}
              maxLength={6}
              title="Enter a valid 6-digit pincode"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input"
              placeholder="201001"
            />
            <span className="mt-1 block text-xs text-ink-400">Places your dot on the map</span>
          </label>
          {isMinor && (
            <label className="block">
              <span className="label label-required">Parent / Guardian mobile</span>
              <input
                required
                id="guardianPhone"
                name="guardian-tel"
                type="tel"
                inputMode="numeric"
                autoComplete="off"
                pattern={MOBILE_PATTERN}
                maxLength={10}
                title="Enter a valid 10-digit mobile number"
                value={guardianPhone}
                onChange={(e) => setGuardianPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="input"
                placeholder="98xxxxxxxx"
              />
              <span className="mt-1 block text-xs text-ink-500">Required since you are under 18.</span>
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {isMinor && (
            <label className="block">
              <span className="label">Guardian is my</span>
              <select
                id="guardianRelation"
                name="guardian-relation"
                autoComplete="off"
                value={guardianRelation}
                onChange={(e) => setGuardianRelation(e.target.value)}
                className="input"
              >
                <option value="">Select</option>
                {GUARDIAN_RELATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="label label-required">Area / Locality</span>
            <input
              id="locality"
              name="locality"
              autoComplete="address-level3"
              value={locality}
              onChange={(e) => setLocality(e.target.value)}
              className="input"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={useCurrentLocation}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-700 underline-offset-4 hover:underline"
        >
          <LocateFixed size={15} /> {locating ? 'Locating...' : coords ? 'Location set' : 'Use my current location'}
        </button>

        <div>
          <span className="label label-required">What are you looking for?</span>
          <div className="flex flex-wrap gap-2">
            {VISIBLE_LOOKING_FOR.map((opt) => (
              <button
                key={opt.category}
                type="button"
                onClick={() => toggleLookingFor(opt.category)}
                className={`chip ${lookingFor.has(opt.category) ? 'chip-active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} />

        <ErrorMessage message={error} />

        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={onBack} className="btn-ghost">Back</button>
          <button type="submit" disabled={submitting} className="btn-sun">
            {submitting ? 'Creating account...' : 'Join the network'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
